/**
 * PostProjectUpdateDialog — R148g · Feb 2026
 * ------------------------------------------
 * Popup wrapper around the Project → Feed status-update composer. Reused
 * from Project Intelligence (action button on each project card) and any
 * other surface that needs a quick "post an update on THIS project"
 * shortcut without navigating the user away from where they are.
 *
 * Matches the ProjectStatusUpdatesTab composer look-and-feel:
 *   · SmartRichText body (mentions + AI polish)
 *   · Internal / client visibility toggle
 *   · Draft-with-AI button
 *   · Post button
 *
 * Voice-to-text is intentionally omitted — mic prompts in a modal feel
 * hostile. Full tab (/app/projects/:id?tab=updates) has the mic.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import SmartRichText from "@/components/SmartRichText";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Sparkles, Loader2, Eye, Lock, X } from "lucide-react";

export default function PostProjectUpdateDialog({ project, onClose, onPosted }) {
  const { activeWorkspaceId } = useAuth();
  const [body, setBody] = useState("");
  const [visibility, setVisibility] = useState("internal");
  const [members, setMembers] = useState([]);
  const [mentionIds, setMentionIds] = useState([]);
  const [showMentionPicker, setShowMentionPicker] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const wsId = activeWorkspaceId || project?.workspace_id;
    if (!wsId) return;
    let cancel = false;
    api.get(`/workspaces/${wsId}/members`)
      .then(({ data }) => {
        if (cancel) return;
        setMembers(Array.isArray(data) ? data : (data?.members || []));
      })
      .catch(() => {});
    return () => { cancel = true; };
  }, [activeWorkspaceId, project?.workspace_id]);

  const memberOptions = members
    .map((m) => ({ id: m.user_id || m.id, name: m.name || m.email || "—" }))
    .filter((m) => !!m.id)
    .sort((a, b) => a.name.localeCompare(b.name));

  const toggleMention = (uid) => {
    setMentionIds((prev) => prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]);
  };

  const aiDraft = async () => {
    if (!project?.id) return;
    setDrafting(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/status-updates/ai-draft`);
      setBody(data.body_html || "");
      toast.success("Draft ready — review and post");
    } catch (e) { toast.error(apiErrorMessage(e, "AI draft failed")); }
    finally { setDrafting(false); }
  };

  const post = async () => {
    const html = (body || "").trim();
    if (!html || html === "<p></p>") { toast.error("Write something first"); return; }
    setSaving(true);
    try {
      await api.post(`/projects/${project.id}/status-updates`, {
        body_html: body,
        visibility,
        kind: "manual",
        mention_user_ids: mentionIds,
      });
      toast.success(mentionIds.length ? `Update posted · ${mentionIds.length} notified` : "Update posted");
      onPosted?.();
      onClose?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Post failed")); }
    finally { setSaving(false); }
  };

  if (!project) return null;

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        className="max-w-2xl w-[95vw] max-h-[92vh] overflow-y-auto rounded-none"
        data-testid="post-project-update-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Post update · <span className="font-normal">{project.name}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {/* Visibility toggle */}
          <div className="flex items-center gap-1.5">
            <button type="button" onClick={() => setVisibility("internal")}
              className={`text-[10px] px-2 py-1 border flex items-center gap-1 ${visibility === "internal" ? "border-primary bg-primary/10" : "border-border hover:bg-muted"}`}
              data-testid="ppud-visibility-internal">
              <Lock className="w-3 h-3" /> Internal
            </button>
            <button type="button" onClick={() => setVisibility("client")}
              className={`text-[10px] px-2 py-1 border flex items-center gap-1 ${visibility === "client" ? "border-primary bg-primary/10" : "border-border hover:bg-muted"}`}
              data-testid="ppud-visibility-client">
              <Eye className="w-3 h-3" /> Visible to client
            </button>
          </div>

          {/* Rich text body */}
          <SmartRichText
            value={body}
            onChange={setBody}
            mediaMode="upload"
            workspaceId={activeWorkspaceId || project?.workspace_id}
            minRows={4}
            maxHeight={340}
            placeholder="What moved forward today? Wins · risks · what's next. Type `/` for formatting, `@` to notify, `/ai` to polish."
            testid="ppud-body"
            mentionSuggestions={memberOptions.map((m) => ({ id: m.id, label: m.name }))}
            onMentionPick={(uid) => { if (!mentionIds.includes(uid)) setMentionIds((prev) => [...prev, uid]); }}
          />

          {/* Notify chips + picker */}
          <div className="relative">
            <div className="flex items-center gap-1.5 flex-wrap">
              <button
                type="button"
                onClick={() => setShowMentionPicker((v) => !v)}
                className="text-[10px] px-2 py-1 border border-border hover:bg-muted flex items-center gap-1"
                data-testid="ppud-mention-toggle"
                title="Notify teammates — in-app only."
              >
                @ Notify {mentionIds.length > 0 && <span className="ml-1 px-1 bg-primary text-primary-foreground font-bold">{mentionIds.length}</span>}
              </button>
              {mentionIds.map((uid) => {
                const m = memberOptions.find((x) => x.id === uid);
                return (
                  <span key={uid} className="text-[10px] px-1.5 py-0.5 border border-primary/40 bg-primary/10 text-primary flex items-center gap-1" data-testid={`ppud-mention-chip-${uid}`}>
                    @{m?.name || uid.slice(0, 6)}
                    <button type="button" onClick={() => toggleMention(uid)} className="hover:text-destructive" aria-label={`Remove mention`}>
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </span>
                );
              })}
            </div>
            {showMentionPicker && (
              <div className="absolute left-0 top-full mt-1 z-20 border border-border bg-background shadow-xl max-h-56 overflow-y-auto w-64" data-testid="ppud-mention-picker">
                {memberOptions.length === 0 ? (
                  <div className="p-3 text-xs text-muted-foreground text-center">No teammates found.</div>
                ) : memberOptions.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => toggleMention(m.id)}
                    className={`w-full text-left px-3 py-1.5 text-xs hover:bg-muted flex items-center gap-2 border-b border-border/40 last:border-b-0 ${mentionIds.includes(m.id) ? "bg-primary/10" : ""}`}
                    data-testid={`ppud-mention-option-${m.id}`}
                  >
                    <span className={`w-3 h-3 border ${mentionIds.includes(m.id) ? "bg-primary border-primary" : "border-border"}`} />
                    <span className="truncate">{m.name}</span>
                  </button>
                ))}
                <div className="px-3 py-2 border-t border-border bg-muted/30 text-[10px] text-muted-foreground">
                  In-app notifications only.
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" size="sm" className="rounded-none mr-auto" onClick={aiDraft} disabled={drafting} data-testid="ppud-ai-draft">
            {drafting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
            Draft with AI
          </Button>
          <Button variant="ghost" size="sm" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button size="sm" className="rounded-none" onClick={post} disabled={saving || !body.trim()} data-testid="ppud-post">
            {saving ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Posting…</> : "Post update"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
